<template>
  <div class="col-12 col-lg-12">
    <div class="card mb-5" style="margin-top: 1rem; overflow: visible">
      <!-- Card Header -->
      <div class="card-header p-0 position-relative mt-n3 mx-2 mx-sm-3 z-index-2" style="height: 60px;">
        <div class="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
          <div class="d-flex justify-content-between align-items-center px-2 px-sm-3">
            <h6 class="text-white text-capitalize mb-0 fs-6 fs-sm-5">
              {{ $t('examScheduleSettings.title', { group: groupName }) }}
            </h6>
          </div>
        </div>
      </div>

      <!-- Card Body -->
      <div class="card-body px-2 px-sm-4 pb-3 pb-sm-4 pt-3">
        <!-- Exam Settings Section -->
        <div class="mb-4">
          <h5 class="mb-2 mb-sm-3 border-bottom pb-2">{{ $t('examScheduleSettings.examSettings') }}</h5>
          <div class="row g-2 g-sm-3">
            <div class="col-12 col-sm-6">
              <label class="form-label">{{ $t('examScheduleSettings.examType') }} <span class="text-danger">*</span></label>
             <select v-model="examConfig.exam_type_id" class="form-select borderless-input" @change="onExamConfigChange" required>
  <option value="">{{ $t('examScheduleSettings.chooseExamType') }}</option>
  <option v-for="t in $store.getters['examTypes/active']" :key="t.id" :value="t.id">{{ t.name }}</option>
</select>

            </div>
            
            <div class="col-12 col-sm-6">
              <label class="form-label">{{ $t('examScheduleSettings.examDate') }} <span class="text-danger">*</span></label>
              <input
                v-model="examConfig.exam_date"
                type="date"
                class="form-control borderless-input"
                @change="onExamConfigChange"
                required
              />
            </div>
          </div>
          
          <!-- Status Alert -->
          <div v-if="examConfig.exam_type_id && examConfig.exam_date" class="alert alert-primary mt-3 d-flex align-items-center py-2">
            <i class="fas fa-info-circle me-2"></i>
          <div class="small">
  <strong>{{ $t('examScheduleSettings.statusSelected') }}</strong>
 
  {{ formatDate(examConfig.exam_date) }} -  {{ selectedExamType?.name || '' }} 
</div>
          </div>
          
          <div v-else class="alert alert-info mt-3 d-flex align-items-center py-2">
            <i class="fas fa-exclamation-triangle me-2"></i>
            <div class="small">
              {{ $t('examScheduleSettings.statusNotSelected') }}
            </div>
          </div>
        </div>

        <!-- Schedule Settings Section -->
        <div class="mb-4">
          <h5 class="mb-2 mb-sm-3 border-bottom pb-2">{{ $t('examScheduleSettings.tableSettings') }}</h5>
          <div class="row g-2 g-sm-3">
            <div class="col-6 col-sm-3">
              <label class="form-label small">{{ $t('examScheduleSettings.startTime') }}</label>
              <input
                v-model="scheduleConfig.start_time"
                type="time"
                class="form-control borderless-input"
                @change="generatePeriods"
              />
            </div>
            
            <div class="col-6 col-sm-3">
              <label class="form-label small">{{ $t('examScheduleSettings.periodDuration') }}</label>
              <input
                v-model.number="scheduleConfig.period_duration"
                type="number"
                min="30"
                max="180"
                class="form-control borderless-input"
                @change="generatePeriods"
                :placeholder="$t('examScheduleSettings.periodDurationPlaceholder')"
              />
            </div>
            
            <div class="col-6 col-sm-3">
              <label class="form-label small">{{ $t('examScheduleSettings.totalPeriods') }}</label>
              <select
                v-model.number="scheduleConfig.total_periods"
                class="form-select borderless-input"
                @change="generatePeriods"
              >
                <option value="2">2</option>
                <option value="3">3</option>
                <option value="4">4</option>
                <option value="5">5</option>
                <option value="6">6</option>
              </select>
            </div>
            
            <div class="col-6 col-sm-3">
              <label class="form-label small">{{ $t('examScheduleSettings.breakDuration') }}</label>
              <input
                v-model.number="scheduleConfig.break_duration"
                type="number"
                min="15"
                max="60"
                class="form-control borderless-input"
                @change="generatePeriods"
                :placeholder="$t('examScheduleSettings.breakDurationPlaceholder')"
              />
            </div>
            
            <div class="col-12 col-sm-3">
              <label class="form-label small">{{ $t('examScheduleSettings.breakAfterPeriod') }}</label>
              <select
                v-model.number="scheduleConfig.break_after_period"
                class="form-select borderless-input"
                @change="generatePeriods"
              >
                <option value="1">{{ $t('examScheduleSettings.breakAfterPeriodOptions.1') }}</option>
                <option value="2">{{ $t('examScheduleSettings.breakAfterPeriodOptions.2') }}</option>
                <option value="3">{{ $t('examScheduleSettings.breakAfterPeriodOptions.3') }}</option>
              </select>
            </div>
          </div>
        </div>

        <!-- Exam Schedule Table -->
        <div v-if="examConfig.exam_type_id && examConfig.exam_date">
          <div class="d-flex justify-content-between align-items-center mb-2 mb-sm-3 border-bottom pb-2">
            <h5 class="mb-0 fs-6 fs-sm-5">{{ $t('examScheduleSettings.manageSchedule') }}</h5>
         
          </div>
          
          <!-- Mobile View -->
          <div class="d-block d-lg-none">
            <div v-for="day in days" :key="'mobile-' + day" class="mb-4">
              <h6 class="fw-bold text-center mb-2">{{ $t('examScheduleSettings.days.' + day) }}</h6>
              <div class="list-group">
                <div 
                  v-for="period in getPeriodsForDay(day)"
                  :key="'mobile-' + day + '-' + period.value"
                  class="list-group-item py-2"
                  :class="{
                    'list-group-item-primary': hasExamSchedule(day, period.backendValue),
                    'list-group-item-secondary': period.value === $t('examScheduleSettings.break'),
                    'cursor-pointer': period.value !== $t('examScheduleSettings.break')
                  }"
                  @click="period.value !== $t('examScheduleSettings.break') && handleCellClick(day, period.value, period.backendValue)"
                >
                  <div class="d-flex justify-content-between align-items-center">
                    <span class="fw-bold">{{ period.label }}</span>
                    <small class="text-muted">{{ period.time }}</small>
                  </div>
                  
                  <template v-if="period.value === $t('examScheduleSettings.break')">
                    <div class="text-center mt-1">
                      <strong>{{ $t('examScheduleSettings.break') }}</strong>
                    </div>
                  </template>
                  <template v-else>
                    <div v-if="hasExamSchedule(day, period.backendValue)" class="mt-2">
                      <div class="text-primary fw-bold">
                        {{ getSubjectName(day, period.backendValue) }}
                      </div>
                      <div class="d-flex justify-content-center gap-2 mt-2">
                        <button
                          class="btn btn-icon btn-sm btn-outline-secondary py-1 px-2"
                          @click.stop="editExamSchedule(day, period.backendValue)"
                          :title="$t('examScheduleSettings.edit')"
                          v-can="'exam_schedules.update'"
                        >
                          <i class="fas fa-edit"></i>
                        </button>
                        <button
                          class="btn btn-icon btn-sm btn-outline-danger py-1 px-2"
                          @click.stop="deleteExamSchedule(day, period.backendValue)"
                          :title="$t('examScheduleSettings.delete')"
                          v-can="'exam_schedules.delete'"
                        >
                          <i class="fas fa-trash"></i>
                        </button>
                      </div>
                    </div>
                    <button
                      v-else
                      class="btn btn-sm btn-outline-primary w-100 mt-2 py-1"
                      @click.stop="openAddDialog(day, period.value, period.backendValue)"
                      v-can="'exam_schedules.create'"
                    >
                      <i class="fas fa-plus me-1"></i> {{ $t('examScheduleSettings.addExam') }}
                    </button>
                  </template>
                </div>
              </div>
            </div>
          </div>

          <!-- Desktop View -->
          <div class="d-none d-lg-block">
            <div class="table-responsive" id="exam-schedule-to-print">
              <table class="table table-bordered text-center mb-0">
                <thead class="bg-light">
                  <tr>
                    <th style="width: 120px">{{ $t('examScheduleSettings.dayPeriod') }}</th>
                    <th v-for="period in periodsWithTimes" :key="period.value">
                      {{ period.label }}<br />
                      <small class="text-muted">{{ period.time }}</small>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="day in days" :key="day">
                    <td class="bg-light fw-bold">{{ $t('examScheduleSettings.days.' + day) }}</td>
                    
                    <template v-if="day === 'السبت' && !saturdayHasExamSchedules">
                      <td 
                        :colspan="periodsWithTimes.length" 
                        class="empty-day cursor-pointer py-4"
                        @click="openAddDialogForEmptySaturday"
                      >
                        <div class="text-muted">
                          <i class="fas fa-plus-circle mb-2 fs-4"></i><br>
                          {{ $t('examScheduleSettings.emptySaturday') }}
                        </div>
                      </td>
                    </template>
                    
                    <template v-else>
                      <td
                        v-for="period in getPeriodsForDay(day)"
                        :key="period.value"
                        class="align-middle py-2"
                        :class="{
                          'bg-light': period.value === $t('examScheduleSettings.break'),
                          'has-schedule': hasExamSchedule(day, period.backendValue),
                          'cursor-pointer': period.value !== $t('examScheduleSettings.break')
                        }"
                        @click="period.value !== $t('examScheduleSettings.break') && handleCellClick(day, period.value, period.backendValue)"
                      >
                        <template v-if="period.value === $t('examScheduleSettings.break')">
                          <strong>{{ $t('examScheduleSettings.break') }}</strong>
                        </template>
                        <template v-else>
                          <div v-if="hasExamSchedule(day, period.backendValue)" class="schedule-content">
                            <div class="text-primary fw-bold mb-1">
                              {{ getSubjectName(day, period.backendValue) }}
                            </div>
                            <div class="d-flex justify-content-center gap-2">
                              <button
                                class="btn btn-icon btn-sm btn-outline-secondary py-1 px-2"
                                @click.stop="editExamSchedule(day, period.backendValue)"
                                :title="$t('examScheduleSettings.edit')"
                                v-can="'exam_schedules.update'"
                              >
                                <i class="fas fa-edit"></i>
                              </button>
                              <button
                                class="btn btn-icon btn-sm btn-outline-danger py-1 px-2"
                                @click.stop="deleteExamSchedule(day, period.backendValue)"
                                :title="$t('examScheduleSettings.delete')"
                                v-can="'exam_schedules.delete'"
                              >
                                <i class="fas fa-trash"></i>
                              </button>
                            </div>
                          </div>
                          <button
                            v-else
                            class="btn btn-sm btn-outline-primary py-1 px-3"
                            @click.stop="openAddDialog(day, period.value, period.backendValue)"
                            v-can="'exam_schedules.create'"
                          >
                            <i class="fas fa-plus me-1"></i> {{ $t('examScheduleSettings.addExam') }}
                          </button>
                        </template>
                      </td>
                    </template>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="d-flex flex-column flex-sm-row gap-2 mt-4">
          <button
            type="button"
            class="btn btn-primary flex-grow-1 btn-sm o py-2"
            @click="saveScheduleSettings"
            :disabled="loading"
            v-can="'exam_schedules.update'"
          >
            <template v-if="loading">
              <i class="fas fa-spinner fa-spin me-2"></i>
              {{ $t('examScheduleSettings.actions.saveLoading') }}
            </template>
            <template v-else>
              <i class="fas fa-save me-2"></i>
              {{ $t('examScheduleSettings.actions.save') }}
            </template>
          </button>
          <button
            type="button"
            class="btn btn-outline-secondary btn-sm flex-grow-1  py-2"
            @click="handleClose"
            :disabled="loading"
          >
            <i class="fas fa-times me-2"></i>
            {{ $t('examScheduleSettings.actions.cancel') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modals -->
  <ExamScheduleFormDialog
  ref="examScheduleFormDialog"
  :class-id="classId"
   :class-name="className"  
  :group-id="groupId"
  :group-name="groupName"
  :exam-type-id="examConfig.exam_type_id"
  :exam-types="$store.getters['examTypes/active']"
  :exam-date="examConfig.exam_date"
  @refresh="fetchExamSchedules"
/>

<DeleteDialog
  ref="deleteDialog"
  entity-name="جدول الامتحان"
  @confirm="handleDeleteExamSchedule"
/>  </div>
</template>
<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";
import { useToast } from "vue-toastification";
import { useRoute, useRouter } from "vue-router";
import ExamScheduleFormDialog from "@/components/features/schedules/ExamScheduleForm.vue";
import DeleteDialog from "@/components/common/DeleteDialog.vue";

const { t, locale } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const toast = useToast();

const className = ref('');
const classId = route.params.classId;
const groupId = route.params.groupId;
const groupName = ref('');
const examSchedules = ref([]);
const loading = ref(false);
const examScheduleFormDialog = ref(null);
const deleteDialog = ref(null);
const selectedExamSchedule = ref(null);
const selectedExamType = computed(() =>
  store.getters['examTypes/active'].find(t => t.id == examConfig.value.exam_type_id)
);
const examConfig = ref({
  exam_type_id: '',
  exam_date: ''
});

const scheduleConfig = ref({
  start_time: "08:00",
  period_duration: 90,
  total_periods: 4,
  break_duration: 30,
  break_after_period: 2
});

const days = ["السبت", "الأحد", "الاثنين", "الثلاثاء", "الأربعاء", "الخميس"];
const BREAK_BACKEND_VALUE = "استراحة";

// Period names for UI and backend mapping
const periodBackendNames = {
  1: "الاولى",
  2: "الثانية",
  3: "الثالثة",
  4: "الرابعة",
  5: "الخامسة",
  6: "السادسة",
  7: "السابعة",
  8: "الثامنة"
};

const periodsWithTimes = computed(() => {
  const periods = [];
  const startTime = new Date(`2000-01-01T${scheduleConfig.value.start_time}`);
  const periodDuration = scheduleConfig.value.period_duration;
  const breakDuration = scheduleConfig.value.break_duration;
  const totalPeriods = scheduleConfig.value.total_periods;
  const breakAfterPeriod = scheduleConfig.value.break_after_period;
  
  let currentTime = new Date(startTime.getTime());
  
  for (let i = 1; i <= totalPeriods; i++) {
    const periodEnd = new Date(currentTime.getTime() + periodDuration * 60000);

    periods.push({
      value: getPeriodDisplayName(i),
      backendValue: getBackendPeriodName(i),
      label: t('examScheduleSettings.periodLabel', { num: getPeriodDisplayName(i) }),
      time: `${formatTime(currentTime)} - ${formatTime(periodEnd)}`
    });

    if (i === breakAfterPeriod) {
      const breakStart = new Date(periodEnd.getTime());
      const breakEnd = new Date(breakStart.getTime() + breakDuration * 60000);
      periods.push({
        value: t('examScheduleSettings.break'),
        backendValue: BREAK_BACKEND_VALUE,
        label: t('examScheduleSettings.break'),
        time: `${formatTime(breakStart)} - ${formatTime(breakEnd)}`
      });
      currentTime = new Date(breakEnd.getTime());
    } else {
      currentTime = new Date(periodEnd.getTime());
    }
  }
  return periods;
});

const saturdayHasExamSchedules = computed(() => {
  if (!Array.isArray(examSchedules.value)) return false;
  return examSchedules.value.some(s => s.day === "السبت");
});

const getPeriodsForDay = (day) => {
  if (day === "السبت" && !saturdayHasExamSchedules.value) {
    return [];
  }
  return periodsWithTimes.value;
};

function getPeriodDisplayName(number) {
  // UI period name from i18n
  return t(`examScheduleSettings.periodNames.${number}`, number.toString());
}

function getBackendPeriodName(number) {
  // Always Arabic backend key
  return periodBackendNames[number] || number.toString();
}

function formatTime(date) {
  return date.toLocaleTimeString("en-US", { hour12: false, hour: "2-digit", minute: "2-digit" });
}



function formatDate(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString(locale.value || 'ar', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

function generatePeriods() {
  // Just triggers reactivity for periodsWithTimes
}

function onExamConfigChange() {
  if (examConfig.value.exam_type_id && examConfig.value.exam_date) {
    fetchExamSchedules();
  }
}

async function saveScheduleSettings() {
  try {
    loading.value = true;
    const configToSave = {
      ...scheduleConfig.value,
      ...examConfig.value
    };
    localStorage.setItem(`exam_schedule_config_${classId}_${groupId}`, JSON.stringify(configToSave));
    toast.success(t('examScheduleSettings.alerts.saveSuccess'));

    setTimeout(() => {
      router.push({
        name: 'exams.schedule',
        params: { classId, groupId }
      });
    }, 1000);
  } catch (error) {
    toast.error(t('examScheduleSettings.alerts.saveFailed'));
  } finally {
    loading.value = false;
  }
}

function handleClose() {
  router.push({
    name: 'exams.schedule',
    params: { classId, groupId }
  });
}

function loadScheduleSettings() {
  try {
    const saved = localStorage.getItem(`exam_schedule_config_${classId}_${groupId}`);
    if (saved) {
      const config = JSON.parse(saved);
      scheduleConfig.value = {
        start_time: config.start_time || "08:00",
        period_duration: config.period_duration || 90,
        total_periods: config.total_periods || 4,
        break_duration: config.break_duration || 30,
        break_after_period: config.break_after_period || 2
      };
      examConfig.value = {
        exam_type_id: config.exam_type_id || '',
        exam_date: config.exam_date || ''
      };
    }
  } catch {
    toast.error(t('examScheduleSettings.alerts.loadFailed'));
  }
}

async function fetchExamSchedules() {
  if (!examConfig.value.exam_type_id || !examConfig.value.exam_date) {
    examSchedules.value = [];
    return;
  }
  try {
    loading.value = true;
    const response = await store.dispatch("examSchedules/fetchClassExamSchedules", {
      classId,
      exam_type_id: examConfig.value.exam_type_id,
      exam_date: examConfig.value.exam_date
    });
    examSchedules.value = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [];
  } catch (error) {
    toast.error(error.response?.data?.message || t('examScheduleSettings.alerts.loadFailed'));
    examSchedules.value = [];
  } finally {
    loading.value = false;
  }
}

function hasExamSchedule(day, period) {
  if (!Array.isArray(examSchedules.value)) return false;
  return examSchedules.value.some((s) => s.day === day && s.period === period);
}

function getSubjectName(day, period) {
  if (!Array.isArray(examSchedules.value)) return "";
  const examSchedule = examSchedules.value.find((s) => s.day === day && s.period === period);
  return examSchedule?.subject?.name || "";
}

function handleCellClick(day, frontendPeriod, backendPeriod) {
  if (backendPeriod === BREAK_BACKEND_VALUE) return; // Ignore break cell clicks
  if (hasExamSchedule(day, backendPeriod)) {
    editExamSchedule(day, backendPeriod);
  } else {
    openAddDialog(day, frontendPeriod, backendPeriod);
  }
}

function openAddDialogForEmptySaturday() {
  const firstPeriod = periodsWithTimes.value[0];
  if (firstPeriod) {
    openAddDialog("السبت", firstPeriod.value, firstPeriod.backendValue);
  }
}

function openAddDialog(day, frontendPeriod, backendPeriod) {
  if (backendPeriod === BREAK_BACKEND_VALUE) return; // Don't process break
  const periodObj = periodsWithTimes.value.find(p => p.value === frontendPeriod && p.backendValue === backendPeriod);
  if (!periodObj) return;
  const [period_start, period_end] = periodObj.time.split(" - ");
  examScheduleFormDialog.value?.openDialog({
    day,
    period: backendPeriod, // Send backend value to API!
    period_start: period_start.trim(),
    period_end: period_end.trim(),
    exam_type_id: examConfig.value.exam_type_id,
    exam_date: examConfig.value.exam_date,
    class_id: classId
  }, () => fetchExamSchedules());
}

function editExamSchedule(day, backendPeriod) {
  const examSchedule = examSchedules.value.find((s) => s.day === day && s.period === backendPeriod);
  if (examSchedule && examScheduleFormDialog.value?.openDialog) {
    examScheduleFormDialog.value.openDialog({
      ...examSchedule,
      period: backendPeriod // Make sure dialog is aware of the backend value
    });
  }
}

function deleteExamSchedule(day, backendPeriod) {
  const examSchedule = examSchedules.value.find((s) => s.day === day && s.period === backendPeriod);
  if (examSchedule) {
    selectedExamSchedule.value = examSchedule;
    deleteDialog.value.openDialog(
      t('examScheduleSettings.deleteDialog.title'),
      t('examScheduleSettings.deleteDialog.message', { subject: examSchedule.subject?.name, day })
    );
  }
}

async function handleDeleteExamSchedule() {
  try {
    await store.dispatch("examSchedules/deleteExamSchedule", {
      classId,
      groupId,
      id: selectedExamSchedule.value.id
    });
    toast.success(t('examScheduleSettings.alerts.deleteSuccess'));
    fetchExamSchedules();
  } catch (error) {
    toast.error(error.response?.data?.message || t('examScheduleSettings.alerts.deleteFailed'));
  }
}

watch(() => [examConfig.value.exam_type_id, examConfig.value.exam_date], () => {
  if (examConfig.value.exam_type_id && examConfig.value.exam_date) {
    fetchExamSchedules();
  }
}, { deep: true });

onMounted(async () => {
  loadScheduleSettings();
  if (examConfig.value.exam_type_id && examConfig.value.exam_date) {
    await fetchExamSchedules();
  }
  if (!store.getters['examTypes/active']?.length) {
    await store.dispatch('examTypes/fetchAll');
  }
});
</script>
